import React from 'react';
import { HelpCircleIcon, AlertCircleIcon } from 'lucide-react';

import UserContext from 'context/userContext';
import { EventType } from 'types/event.types';
import { Button } from '@epoch-ui/components/Button';
import { Label } from '@epoch-ui/components/Form';
import { Checkbox } from '@epoch-ui/components/Checkbox';
import { Switch } from '@epoch-ui/components/Switch/switch';
import {
  AlertBanner,
  AlertBannerIcon,
  AlertBannerDescription
} from '@epoch-ui/components/AlertBanner';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalFooter
} from '@epoch-ui/components/Modal/modal';

import { useEditableEvent } from '../../hooks/useEditableEvent';

type EventUpdateNotificationSettings = {
  sendCalendarEmail: boolean;
  sendSlack: boolean;
  resetRsvps: boolean;
};

interface EventUpdateNotificationSettingsModalProps
  extends React.ComponentPropsWithoutRef<typeof Modal> {
  canResetRsvps: boolean;
  onSubmit: (values: EventUpdateNotificationSettings) => void;
  onCancel: () => void;
}

const EventUpdateNotificationSettingsModal: React.FC<EventUpdateNotificationSettingsModalProps> = ({
  onSubmit,
  onCancel,
  onOpenChange,
  canResetRsvps,
  ...props
}) => {
  const user = React.useContext(UserContext);
  const event = useEditableEvent();

  const eventOrSessionLabel = event.eventType === EventType.MULTI_SESSIONS ? 'session' : 'event';

  const [updateSendSettings, setUpdateSendSettings] =
    React.useState<EventUpdateNotificationSettings>({
      sendCalendarEmail: true,
      sendSlack: true,
      resetRsvps: false
    });

  const usesOutlook = user?.companyUsesOutlookCalendar;
  const calendarText = usesOutlook ? 'Outlook Calendar' : 'Google Calendar';
  const areCalendarInvitesTurnedOff = !event.sendCalendarInvites && !event.calendarNotifications;

  const finalSendCalendarEmail = areCalendarInvitesTurnedOff
    ? false
    : updateSendSettings.sendCalendarEmail || (!!usesOutlook && !areCalendarInvitesTurnedOff);

  return (
    <Modal onOpenChange={onOpenChange} {...props}>
      <ModalContent className="tw-max-w-[560px]">
        <ModalHeader className="tw-mb-5">
          <ModalTitle>Send update notifications?</ModalTitle>
        </ModalHeader>

        {/* Modal body */}
        <div className="tw-flex tw-flex-col tw-gap-6">
          {/* Send notification options */}
          <div className="tw-flex tw-flex-col tw-gap-2">
            {/* Send calendar email */}
            <div className="tw-flex tw-flex-row tw-items-center tw-gap-3">
              <Checkbox
                id="sendCalendarEmail"
                checked={finalSendCalendarEmail}
                disabled={usesOutlook || areCalendarInvitesTurnedOff}
                onCheckedChange={(checked) => {
                  if (usesOutlook || areCalendarInvitesTurnedOff) {
                    // disabled
                    return;
                  }

                  setUpdateSendSettings({
                    ...updateSendSettings,
                    sendCalendarEmail: checked as boolean
                  });
                }}
              />
              <Label
                htmlFor="sendCalendarEmail"
                className="tw-mb-0 tw-flex tw-flex-col tw-gap-[2px] tw-text-sm tw-font-normal tw-leading-5"
              >
                {/* Label text */}
                <span>
                  Email from {calendarText}{' '}
                  {!!event.createdBy && (
                    <span className="tw-text-muted-foreground">
                      ({event.createdBy.firstName} {event.createdBy.lastName})
                    </span>
                  )}
                </span>

                {/* Description text(s) */}
                {usesOutlook && !areCalendarInvitesTurnedOff && (
                  <span className="tw-text-xs tw-leading-4 tw-text-muted-foreground">
                    Required for Outlook
                  </span>
                )}
                {areCalendarInvitesTurnedOff && (
                  <span className="tw-text-xs tw-leading-4 tw-text-muted-foreground">
                    No calendar event created for this {eventOrSessionLabel}
                  </span>
                )}
              </Label>
            </div>

            {/* Send slack */}
            <div className="tw-flex tw-flex-row tw-items-center tw-gap-3">
              <Checkbox
                id="sendSlack"
                checked={updateSendSettings.sendSlack}
                onCheckedChange={(checked) =>
                  setUpdateSendSettings({ ...updateSendSettings, sendSlack: checked as boolean })
                }
              />
              <Label htmlFor="sendSlack" className="tw-mb-0 tw-text-sm tw-font-normal tw-leading-5">
                Slack
              </Label>
            </div>
          </div>

          {/* Reset RSVP */}
          {canResetRsvps && (
            <div className="tw-flex tw-flex-col tw-gap-4">
              <div className="tw-flex tw-flex-row tw-items-center tw-gap-2">
                <Switch
                  id="resetRsvp"
                  checked={updateSendSettings.resetRsvps}
                  onCheckedChange={(checked) =>
                    setUpdateSendSettings({ ...updateSendSettings, resetRsvps: checked as boolean })
                  }
                />
                <Label
                  htmlFor="resetRsvp"
                  className="tw-mb-0 tw-text-sm tw-font-normal tw-leading-5"
                >
                  Change RSVPs to pending
                </Label>
              </div>

              {updateSendSettings.resetRsvps && (
                <AlertBanner variant="default">
                  <AlertBannerIcon>
                    <AlertCircleIcon />
                  </AlertBannerIcon>
                  <AlertBannerDescription>
                    If you choose to send notifications, guests will be notified to register for the{' '}
                    {eventOrSessionLabel} again. Event questionnaire responses will be restored.
                  </AlertBannerDescription>
                </AlertBanner>
              )}
            </div>
          )}
        </div>

        <ModalFooter className="tw-mt-5">
          <a
            target="_blank"
            rel="noreferrer"
            className="tw-mr-auto tw-flex tw-items-center tw-text-muted-foreground hover:tw-text-muted-foreground"
            href="https://help.epochapp.com/articles/how-do-i-control-update-notifications-when-i-edit-an-event"
          >
            <HelpCircleIcon size={16} />
            <span className="tw-ml-1">Help</span>
          </a>

          {canResetRsvps ? (
            // Update/Cancel
            <>
              <Button size="sm" variant="secondary" onClick={() => onCancel()}>
                Cancel
              </Button>
              <Button
                size="sm"
                variant="primary"
                onClick={() =>
                  onSubmit({
                    ...updateSendSettings,
                    sendCalendarEmail: finalSendCalendarEmail
                  })
                }
              >
                Update
              </Button>
            </>
          ) : (
            // Send/Don't send
            <>
              <Button
                size="sm"
                variant="secondary"
                onClick={() =>
                  onSubmit({
                    ...updateSendSettings,
                    sendCalendarEmail: false,
                    sendSlack: false
                  })
                }
              >
                Don’t send
              </Button>
              <Button
                size="sm"
                variant="primary"
                disabled={!updateSendSettings.sendCalendarEmail && !updateSendSettings.sendSlack}
                onClick={() =>
                  onSubmit({
                    ...updateSendSettings,
                    sendCalendarEmail: finalSendCalendarEmail
                  })
                }
              >
                Send
              </Button>
            </>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export {
  EventUpdateNotificationSettingsModal,
  type EventUpdateNotificationSettingsModalProps,
  type EventUpdateNotificationSettings
};
